'use es6';

import { Map as ImmutableMap, List, fromJS } from 'immutable';
import { getPrimaryDataset } from 'reporting-data/v2/dataset/datasetGetters';
import { getPropertyLabel } from 'reporting-data/v2/dataset/datasetRecords';
import { AccumulateAlong, AccumulateTransform } from 'reporting-visualizations/transforms/transformRecords';
import { Axis, GridLineDashStyles, Style, XYVisualization } from 'reporting-visualizations/visualizations/xy/xyRecords';
import { getDataLabelsStyle, getStackLabelStyle } from '../../../shared/lib/style';
import { toVisualizationOrder } from '../../modify/sorts/report-sort-utils';
import { toSortDependencyAlias } from '../../modify/sorts/sort-utils';
import { AxisModes, MarkTypes } from '../../schema/visuals/combo-visual-records';
import { getDateResolution, getReportBreakdownEncoding, getReportXEncoding, getReportY1Encoding, getReportY2Encoding } from '../../utils/report-utils';
import { getEncodingColumnAlias, getEncodingFormatParameters, getEncodingFormatType } from '../../utils/visual-utils';
import { applyScaleToAxis } from '../apply-scale-to-axis';
import { applyTypeToAxis } from '../apply-type-to-axis';
import { computeAssignments, computePropertyAssignments } from '../color/assign';
import { collectDimensionBuckets, collectMeasureBuckets } from '../color/bucket';
import { DefaultY2 } from '../color/palettes';
import { getColorPalette } from '../color/utils';
import { getDeprecatedDefaultPalettes } from '../color/palettes';
import { deriveFixedIds, fixLOD } from '../fix-lod';
import { getTodayLine, processReportStacking } from './shared';
import CssVariablesTheme from 'foundations-theming/css-vars-theme';
const reportToVizMarkType = {
  [MarkTypes.AREA]: 'area',
  [MarkTypes.LINE]: 'line',
  [MarkTypes.BAR]: 'bar'
};
const defaultMarkType = 'bar';
const convertToVizMarkType = reportMarkType => {
  const vizMarkType = reportToVizMarkType[reportMarkType];
  if (vizMarkType === undefined) {
    console.warn(`Unrecognized mark type \`${reportMarkType}\`, defaulting to \`${defaultMarkType}\`!`);
    return defaultMarkType;
  }
  return vizMarkType;
};
const columnSupportsShowTodayLine = (column, columnMarkType) => {
  return column && (columnMarkType === MarkTypes.LINE || columnMarkType === MarkTypes.AREA);
};
const getXColumnId = report => {
  const x = getReportXEncoding(report);
  return x && getEncodingColumnAlias(x);
};
const getY1ColumnId = report => {
  const y1 = getReportY1Encoding(report);
  return y1 && getEncodingColumnAlias(y1);
};
const getY2ColumnId = report => {
  const y2 = getReportY2Encoding(report);
  return y2 && getEncodingColumnAlias(y2);
};
const getBreakdownColumnId = report => {
  const breakdown = getReportBreakdownEncoding(report);
  return breakdown && getEncodingColumnAlias(breakdown);
};
const partitionY1Y2Dataset = (report, dataset) => {
  const maybeY1Id = getY1ColumnId(report);
  const y1Dataset = maybeY1Id ? fixLOD(deriveFixedIds(report, maybeY1Id), List.of(maybeY1Id), dataset) : dataset;
  const maybeY2Id = getY2ColumnId(report);
  const y2Dataset = maybeY2Id ? fixLOD(deriveFixedIds(report, maybeY2Id), List.of(maybeY2Id), dataset) : dataset;
  return {
    y1Dataset,
    y2Dataset
  };
};
const addSortDependency = (column, dataset, layerDataset) => {
  if (!column) {
    return layerDataset;
  }
  if (!column.sort || !column.sort.by) {
    return layerDataset;
  }
  const field = column.alias;
  const sortAlias = toSortDependencyAlias(column.alias);
  const grouped = dataset.data.groupBy(row => row.get(field)).map(rows => rows.first().get(sortAlias));
  return layerDataset.update('data', data => data.map(row => row.set(sortAlias, grouped.get(row.get(field)))));
};
function getBlend(axisMode, y1AxisKey, y2AxisKey) {
  return axisMode === AxisModes.BLEND ? y1AxisKey : y2AxisKey;
}
function getY1Layer({
  report,
  breakdownId,
  y1Color,
  dataset,
  y1MarkType,
  stacking,
  y1ShowMarkers,
  xId,
  x,
  y1Id,
  y1AxisKey,
  y1Accumulate,
  accumulateAlong,
  brandKitPalettes,
  palettes
}) {
  const maybeBreakdownColumn = report.columns.get(breakdownId);
  const y1Encoding = getReportY1Encoding(report);
  const y1Column = y1Encoding.column;
  const toY1Color = () => {
    const {
      scheme
    } = getColorPalette(y1Color, brandKitPalettes, (palettes === null || palettes === void 0 ? void 0 : palettes.DEFAULT_PALETTES) || getDeprecatedDefaultPalettes('combo-mapper-y1-fallback'), palettes === null || palettes === void 0 ? void 0 : palettes.DefaultCategorical);
    const order = maybeBreakdownColumn && toVisualizationOrder(maybeBreakdownColumn, dataset);
    const buckets = maybeBreakdownColumn === undefined && y1Column ? collectMeasureBuckets(List.of(y1Column), dataset) : collectDimensionBuckets(maybeBreakdownColumn, dataset);
    const assignments = computeAssignments(buckets, scheme, y1Color.assignments);
    if (assignments.isEmpty()) {
      return undefined;
    }
    const patternAssignments = computePropertyAssignments(buckets, scheme.patterns);
    if (maybeBreakdownColumn === undefined) {
      return {
        default: assignments.first(),
        defaultPattern: patternAssignments && patternAssignments.first()
      };
    }
    return {
      field: breakdownId,
      defined: assignments.toJS(),
      definedPatterns: patternAssignments && patternAssignments.toJS(),
      order
    };
  };
  const y1ColorViz = toY1Color();
  return {
    dataset: 'y1Dataset',
    mark: Object.assign({
      type: convertToVizMarkType(y1MarkType)
    }, y1MarkType !== MarkTypes.LINE ? {
      stacking
    } : {}, {
      marker: {
        enabled: y1ShowMarkers
      }
    }),
    x: xId ? {
      type: 'category',
      field: xId,
      blend: 'bottom',
      order: toVisualizationOrder(x.column, dataset),
      formattingType: getEncodingFormatType(x),
      formattingObject: getEncodingFormatParameters(x)
    } : {
      type: 'scaffold',
      blend: 'bottom'
    },
    y: y1Column ? {
      type: 'linear',
      field: y1Id,
      blend: y1AxisKey,
      fill: true,
      formattingType: getEncodingFormatType(y1Encoding),
      formattingObject: getEncodingFormatParameters(y1Encoding)
    } : {
      type: 'scaffold',
      blend: y1AxisKey
    },
    color: y1ColorViz,
    transform: y1Accumulate ? [AccumulateTransform({
      groupBy: [breakdownId],
      field: y1Id,
      along: accumulateAlong
    })] : []
  };
}
function getY2Layer({
  report,
  y2Color,
  dataset,
  y2MarkType,
  stacking,
  y2ShowMarkers,
  xId,
  x,
  y2Id,
  axisMode,
  y1AxisKey,
  y2AxisKey,
  y2Accumulate,
  accumulateAlong,
  brandKitPalettes,
  palettes
}) {
  const y2Encoding = getReportY2Encoding(report);
  const y2Column = y2Encoding.column;
  const toY2Color = () => {
    const {
      scheme
    } = getColorPalette(y2Color, brandKitPalettes, (palettes === null || palettes === void 0 ? void 0 : palettes.DEFAULT_PALETTES) || getDeprecatedDefaultPalettes('combo-mapper-y2-fallback'), (palettes === null || palettes === void 0 ? void 0 : palettes.DefaultY2) || DefaultY2);
    const buckets = collectMeasureBuckets(y2Column ? List.of(y2Column) : List(), dataset);
    const assignments = computeAssignments(buckets, scheme, y2Color.assignments);
    const patternAssignments = computePropertyAssignments(buckets, scheme.patterns);
    if (assignments.isEmpty()) {
      return {
        default: CssVariablesTheme['color']['do-not-use-slinky']
      };
    }
    return {
      default: assignments.first(),
      defaultPattern: patternAssignments && patternAssignments.first()
    };
  };
  const y2ColorViz = toY2Color();
  return {
    dataset: 'y2Dataset',
    mark: Object.assign({
      type: convertToVizMarkType(y2MarkType)
    }, y2MarkType !== MarkTypes.LINE ? {
      stacking
    } : {}, {
      marker: {
        enabled: y2ShowMarkers,
        fillColor: CssVariablesTheme['color']['do-not-use-olaf']
      }
    }),
    x: xId ? {
      type: 'category',
      field: xId,
      blend: 'bottom',
      order: toVisualizationOrder(x.column, dataset),
      formattingType: getEncodingFormatType(x),
      formattingObject: getEncodingFormatParameters(x)
    } : {
      type: 'scaffold',
      blend: 'bottom'
    },
    y: y2Column ? {
      type: 'linear',
      field: y2Id,
      blend: getBlend(axisMode, y1AxisKey, y2AxisKey),
      fill: true,
      formattingType: getEncodingFormatType(y2Encoding),
      formattingObject: getEncodingFormatParameters(y2Encoding)
    } : {
      type: 'scaffold',
      blend: getBlend(axisMode, y1AxisKey, y2AxisKey)
    },
    color: y2ColorViz,
    excludeFromLegacyTooltipTotals: true,
    transform: y2Accumulate ? [AccumulateTransform({
      groupBy: [],
      field: y2Id,
      along: accumulateAlong
    })] : []
  };
}
export const mapComboVisual = (report, datasets, reportOptions, {
  fiscalYearMonthName,
  brandKitPalettes,
  palettes
}) => {
  const primaryDataset = getPrimaryDataset(datasets);
  const x = getReportXEncoding(report);
  const breakdown = getReportBreakdownEncoding(report);
  const xId = getXColumnId(report);
  const y1Id = getY1ColumnId(report);
  const y2Id = getY2ColumnId(report);
  const breakdownId = getBreakdownColumnId(report);
  const comboDatasets = partitionY1Y2Dataset(report, primaryDataset);
  const {
    y1Dataset: y1DatasetMissingSortDependencies,
    y2Dataset: y2DatasetMissingSortDependencies
  } = comboDatasets;
  const y1DatasetWithXSort = x ? addSortDependency(x.column, primaryDataset, y1DatasetMissingSortDependencies) : y1DatasetMissingSortDependencies;
  const y1DatasetWithBreakdownSort = breakdown ? addSortDependency(breakdown.column, primaryDataset, y1DatasetWithXSort) : y1DatasetWithXSort;
  const y1Dataset = y1DatasetWithBreakdownSort;
  const y2Dataset = x ? addSortDependency(x.column, primaryDataset, y2DatasetMissingSortDependencies) : y2DatasetMissingSortDependencies;
  const {
    axisMode,
    y1MarkType,
    y2MarkType,
    y1MinMax,
    y2MinMax,
    y1AxisType,
    y2AxisType,
    y1ShowMarkers,
    stacking: stackingOption,
    stackingType,
    y1Stacking,
    showDataLabels,
    y2ShowMarkers,
    showToday,
    y1Color,
    y2Color,
    legendPosition,
    legendWidth,
    y1Accumulate,
    y2Accumulate
  } = report.visual.options;
  const stacking = processReportStacking(stackingOption, stackingType);
  const isComboOnlyLines = y1MarkType === MarkTypes.LINE && y2MarkType === MarkTypes.LINE;
  const migratedStacking = !isComboOnlyLines && (stacking === undefined ? y1Stacking : stacking);
  const layers = [];
  const y1AxisKey = 'y1';
  const y2AxisKey = 'y2';
  const accumulateAlong = AccumulateAlong({
    field: xId,
    /*
    TODO: We only allow ascending accumulations due to
     reporting-snowflake/static-1.49972/js/relational/validate/constraints/visuals/check-sort-is-accumulate-friendly.js:4
     but there is no longer a technical reason why we can't support descending accumulations now.
      We should update this in the near future.
      For now, it is okay to hardcode this
    */
    direction: 'ascending'
  });
  if (xId && !(y1Id || y2Id)) {
    layers.push({
      dataset: 'y1Dataset',
      mark: Object.assign({
        type: convertToVizMarkType(y1MarkType)
      }, y1MarkType !== MarkTypes.LINE ? {
        stacking: migratedStacking
      } : {}, {
        marker: {
          enabled: y1ShowMarkers
        }
      }),
      x: {
        type: 'category',
        field: xId,
        blend: 'bottom',
        order: toVisualizationOrder(x.column, primaryDataset)
      },
      y: {
        type: 'scaffold',
        blend: y1AxisKey
      },
      color: y1Color
    });
  }
  if (y1Id) {
    layers.push(getY1Layer({
      report,
      breakdownId,
      y1Color,
      dataset: primaryDataset,
      y1MarkType,
      stacking: migratedStacking,
      y1ShowMarkers,
      xId,
      x,
      y1Id,
      y1AxisKey,
      y1Accumulate,
      accumulateAlong,
      brandKitPalettes,
      palettes
    }));
  }
  if (y2Id) {
    layers.push(getY2Layer({
      report,
      y2Color,
      dataset: primaryDataset,
      y2MarkType,
      stacking: migratedStacking,
      y2ShowMarkers,
      xId,
      x,
      y2Id,
      axisMode,
      y1AxisKey,
      y2AxisKey,
      y2Accumulate,
      accumulateAlong,
      brandKitPalettes,
      palettes
    }));
  }
  let style = Style();
  style = style.update('y', y => y || ImmutableMap());
  const blendAxisTitle = () => {
    if (axisMode !== AxisModes.BLEND) {
      return style;
    }
    const title = fromJS(layers).reduce((acc, layer) => acc.set(layer.getIn(['y', 'field']), layer), ImmutableMap()).map((layer, field) => {
      const datasetName = layer.get('dataset');
      return getPropertyLabel(comboDatasets[datasetName], field);
    }).join(' / ');
    return style.updateIn(['y', y1AxisKey], (yAxis = Axis({})) => yAxis.set('title', title));
  };
  style = blendAxisTitle();
  const applyMinMax = () => {
    return style.updateIn(['y', y1AxisKey], (axis = Axis({})) => applyScaleToAxis(axis, y1MinMax)).updateIn(['y', y2AxisKey], (axis = Axis({})) => {
      const next = applyScaleToAxis(axis, y2MinMax);
      const y1OrY2HasCustomScale = style.getIn(['y', y1AxisKey, 'startOnTick']) === false || next.startOnTick === false;
      if (y1OrY2HasCustomScale) {
        return next.set('gridLineDashStyle', GridLineDashStyles.solid);
      }
      return next;
    });
  };
  const applyAxisTypes = () => {
    return style.updateIn(['y', y1AxisKey], axis => applyTypeToAxis(axis, y1AxisType)).updateIn(['y', y2AxisKey], axis => applyTypeToAxis(axis, y2AxisType));
  };
  style = applyMinMax();
  style = applyAxisTypes();
  style = style.set('dataLabels', getDataLabelsStyle(migratedStacking, showDataLabels));
  style = style.set('stackLabels', getStackLabelStyle(migratedStacking, showDataLabels));
  style = style.set('legendPosition', legendPosition);
  style = style.set('legendWidth', legendWidth);
  const maybeDateResolution = getDateResolution(xId, report);
  const xColumn = report.columns.get(xId);
  style = style.set('dateResolution', maybeDateResolution);
  if (showToday && xColumn && (columnSupportsShowTodayLine(y1Id, y1MarkType) || columnSupportsShowTodayLine(y2Id, y2MarkType)) && maybeDateResolution) {
    style = style.set('x', {
      bottom: {
        rules: {
          plotLines: [getTodayLine(xColumn, fiscalYearMonthName)]
        }
      }
    });
  }
  return XYVisualization({
    type: 'XY',
    data: {
      primary: primaryDataset,
      y1Dataset,
      y2Dataset
    },
    layers,
    style
  });
};